import styles from './Radios.module.css';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';

import CellTower from '@mui/icons-material/CellTower';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';

import { useEffect, useState, useMemo, useCallback } from 'react';

import { useAuth } from '../../AuthContext';

import { getAllRadios, addRadio, updateRadio, removeRadio } from "../../api.js";

export const Radios = ({ hillCaptain }) => {
    const [ error, setError ] = useState(null);
    const [ radio, setRadio ] = useState({ id: null, name: '', owner: '', serial: '' });
    const [ radios, setRadios ] = useState([]);
    const [ isExpanded, setIsExpanded ] = useState(false);
    const [ editable, setEditable ] = useState(false);

    const { auth, token } = useAuth();

    // memoize radios
    const memoizedRadios = useMemo(() => radios, [ radios ]);

    const sortRadios = useCallback((radioArray) => {
        return radioArray.sort((a, b) => {
            const currentUserName = auth.currentUser.displayName;
            
            const aIsCurrentUser = a.owner === currentUserName;
            const bIsCurrentUser = b.owner === currentUserName;
            
            if (aIsCurrentUser && !bIsCurrentUser) return -1;
            if (!aIsCurrentUser && bIsCurrentUser) return 1;
            
            const ownerCompare = a.owner.localeCompare(b.owner);
            if (ownerCompare === 0) {
                return a.name.localeCompare(b.name);
            }
            
            return ownerCompare;
        });
    }, [ auth.currentUser.displayName ]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const radioData = await getAllRadios(token);

                if (radioData.error) {
                    throw new Error(radioData.error);
                }

                setRadios(sortRadios(radioData));
            } catch (err) {
                setError(err.message);
            }
        };

        fetchData();
    }, [ token, sortRadios ]);

    useEffect(() => {
        if (radio.id) {
            setEditable(true);
        }
    }, [ radio ]);

    const handleOnChange = (e) => {
        const { id, value } = e.target;

        setRadio(prev => ({ ...prev, [ id ]: value }));
    };

    const handleOnChangeOwner = (e) => {
        const { checked } = e.target;

        setRadio(prev => ({ ...prev, owner: checked ? "Ski Patrol" : auth.currentUser.displayName, orgAsOwner: checked }));
    };

    const handleOnCancel = () => {
        setRadio({ id: null, name: '', owner: `${ auth.currentUser.displayName }`, serial: '' });

        setEditable(false);
    };

    const handleOnSave = async () => {
        if (!radio.id) {
            const ret = await addRadio(radio, token);

            if (ret.error) {
                setError(ret.error);

                return;
            }

            setRadios(prev => sortRadios([...prev, { ...radio, id: ret.id }]));
        } else {
            const ret = await updateRadio(radio, token);

            if (ret.error) {
                setError(ret.error);

                return;
            }

            setRadios(sortRadios(radios.map(r => r.id === ret.id ? { ...r, ...radio } : r)));
        }

        setRadio({ id: null, name: '', owner: `${ auth.currentUser.displayName }`, serial: '' });

        setEditable(false);
    };

    const handleOnDelete = async (id) => {
        const success = await removeRadio(id, token);

        if (success) {
            setRadios(radios.filter(r => r.id !== id));
        }
    };

    return (
        <div className={ styles.radioContainer }>
            <div className={ styles.header } onClick={ () => setIsExpanded(!isExpanded) }>
                <span>
                    <Icon component={ CellTower } sx={ { mr: 1 } } />
                    <span>Radios</span>
                </span>
                <span><Icon>{ isExpanded ? <ExpandLess /> : <ExpandMore /> }</Icon></span>
            </div>

            { isExpanded &&
                <div className={ styles.radios }>
                    { !editable && memoizedRadios.length > 0 && memoizedRadios.map(r => {
                        return (
                            <div key={ r.id } className={ `${ styles.item } ${ r.owner === auth.currentUser.displayName ? styles.self : '' }` }>
                                <span>{ r.owner }</span>
                                <span>{ r.name }</span>
                                <span>{ r.serial }</span>
                                <span className={ styles.actions }>
                                    <IconButton onClick={ () => setRadio(r) }>
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={ () => handleOnDelete(r.id) }>
                                        <Delete />
                                    </IconButton>
                                </span>
                            </div>
                        );
                    }) }

                    { editable && (
                        <div className={ styles.item }>
                            <input id="owner" type="text" placeholder="Owner" autoComplete="off" disabled={ !hillCaptain || !radio.orgAsOwner } onChange={ handleOnChange } value={ radio.owner } />
                            <input id="name" type="text" placeholder="Unit Name" autoComplete="off" onChange={ handleOnChange } value={ radio.name } />
                            <input id="serial" type="text" placeholder="Serial Number/ID" autoComplete="off" onChange={ handleOnChange } value={ radio.serial } />

                            <div className={ styles.checkboxContainer }>
                                <label htmlFor="auto">Auto-select for shifts</label>
                                <input id="auto" type="checkbox" placeholder="Auto Select" autoComplete="off" onChange={ handleOnChangeOwner } />
                            </div>

                            { hillCaptain &&
                                <div className={ styles.checkboxContainer }>
                                    <label htmlFor="admin">Set Organization as Owner</label>
                                    <input id="admin" type="checkbox" placeholder="Change Owner" autoComplete="off" onChange={ handleOnChangeOwner } />
                                </div>
                            }
                        </div>
                    ) }

                    { error && (
                        <Alert variant='filled' severity="error" onClose={ () => setError(null) }>{ error }</Alert>
                    ) }

                    <div className={ styles.actions }>
                        { editable ? (
                            <>
                                <Button variant="contained" color="error" disableElevation onClick={ handleOnCancel }>cancel</Button>
                                <Button variant="contained" color="success" disableElevation onClick={ handleOnSave }>save</Button>
                            </>
                        ) : (
                            <Button variant="contained" color="success" disableElevation onClick={ () => setEditable(true) }>add</Button>
                        ) }
                    </div>
                </div>
            }
        </div>
    );
};